<template>
  <div class="coros-evo">
    <div v-if="hoyEntreno" class="coros-hoy rounded-3 p-3 mb-0">
      <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
        <div>
          <div class="small text-uppercase fw-semibold opacity-75">Hoy</div>
          <div v-if="fechaHoy" class="small opacity-75 text-capitalize">{{ fechaHoy }}</div>
        </div>
        <i class="bi bi-calendar3"></i>
      </div>
      <div class="d-flex align-items-center gap-3">
        <div class="coros-hoy-icon rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
          <i class="bi bi-person-walking"></i>
        </div>
        <div class="min-w-0">
          <div class="fw-semibold text-truncate">{{ hoyEntreno.name || 'Entreno planificado' }}</div>
          <div class="small opacity-75">{{ fmtDuration(hoyEntreno.estimated_time_seconds) }}</div>
        </div>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-md-6">
        <div class="coros-metric rounded-3 p-3 h-100">
          <div class="small text-uppercase text-muted fw-semibold mb-2">
            <i class="bi bi-bar-chart-line me-1"></i>Carga semanal
          </div>
          <div class="d-flex align-items-end justify-content-between gap-3">
            <div>
              <div class="coros-metric-value">{{ fmtInt(s.weekLoad) }}</div>
              <div class="small text-muted">
                Recomendada
                <template v-if="s.recommendedMin != null">
                  {{ fmtInt(s.recommendedMin) }}–{{ fmtInt(s.recommendedMax) }}
                </template>
                <template v-else>—</template>
              </div>
            </div>
            <div class="coros-bars" aria-hidden="true">
              <div v-for="bar in (s.weekBars || [])" :key="'w'+bar.date" class="coros-bar-col">
                <div class="coros-bar-track">
                  <div class="coros-bar" :class="{ on: bar.load > 0 }" :style="{ height: barHeight(bar.load, weekMax) }"></div>
                </div>
                <div class="coros-bar-letter">{{ bar.letter }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="coros-metric rounded-3 p-3 h-100">
          <div class="small text-uppercase text-muted fw-semibold mb-2">
            <i class="bi bi-graph-up-arrow me-1"></i>Estado del entrenamiento
          </div>
          <div class="d-flex align-items-end justify-content-between gap-3 mb-3">
            <div>
              <div class="coros-status" :class="'tone-' + (s.status?.tone || 'muted')">
                {{ s.status?.label || '—' }}
              </div>
              <div class="small text-muted">Carga 7 días {{ fmtInt(s.load7d) }}</div>
            </div>
            <div class="coros-bars" aria-hidden="true">
              <div v-for="bar in (s.rollingBars || [])" :key="'r'+bar.date" class="coros-bar-col">
                <div class="coros-bar-track">
                  <div class="coros-bar accent" :class="{ on: bar.load > 0 }" :style="{ height: barHeight(bar.load, rollMax) }"></div>
                </div>
                <div class="coros-bar-letter">{{ bar.letter }}</div>
              </div>
            </div>
          </div>
          <div class="row g-2 text-center">
            <div class="col-4">
              <div class="fw-bold fs-5">{{ fmtInt(s.ati) }}</div>
              <div class="coros-kpi-label">Carga</div>
            </div>
            <div class="col-4">
              <div class="fw-bold fs-5">{{ fmtInt(s.cti) }}</div>
              <div class="coros-kpi-label">Estado forma</div>
            </div>
            <div class="col-4">
              <div class="fw-bold fs-5">
                {{ fmtInt(s.trendPct) }}<span v-if="s.trendPct != null" class="fs-6">%</span>
              </div>
              <div class="coros-kpi-label">Tendencia</div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="coros-metric rounded-3 p-3 h-100">
          <div class="small text-uppercase text-muted fw-semibold mb-2">
            <i class="bi bi-arrow-counterclockwise me-1"></i>Recuperación
          </div>
          <div class="d-flex align-items-center justify-content-between gap-3">
            <div>
              <div class="coros-metric-value text-success">
                {{ fmtInt(s.recoveryPct) }}<span v-if="s.recoveryPct != null" class="fs-5">%</span>
              </div>
              <div v-if="s.recoveryHours != null" class="small text-muted">
                {{ s.recoveryHours }}h hasta recuperación total
              </div>
              <div class="small fw-semibold mt-1">{{ s.recoveryLabel || '—' }}</div>
            </div>
            <div class="coros-gauge">
              <svg viewBox="0 0 120 80" aria-hidden="true">
                <path d="M10 70 A50 50 0 0 1 110 70" fill="none" stroke="#e9ecef" stroke-width="10" stroke-linecap="round" />
                <path
                  d="M10 70 A50 50 0 0 1 110 70"
                  fill="none"
                  stroke="#198754"
                  stroke-width="10"
                  stroke-linecap="round"
                  :stroke-dasharray="gaugeArc"
                  :stroke-dashoffset="gaugeOffset(s.recoveryPct)"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="coros-metric rounded-3 p-3 h-100">
          <div class="small text-uppercase text-muted fw-semibold mb-2">
            <i class="bi bi-person-walking me-1"></i>Estado de carrera
          </div>
          <div class="d-flex align-items-center justify-content-between gap-3">
            <div class="coros-metric-value text-primary">{{ fmtDec(s.runningStatus) }}</div>
            <div class="coros-gauge">
              <svg viewBox="0 0 120 80" aria-hidden="true">
                <path d="M10 70 A50 50 0 0 1 110 70" fill="none" stroke="#e9ecef" stroke-width="10" stroke-linecap="round" />
                <path
                  d="M10 70 A50 50 0 0 1 110 70"
                  fill="none"
                  stroke="#0d6efd"
                  stroke-width="10"
                  stroke-linecap="round"
                  :stroke-dasharray="gaugeArc"
                  :stroke-dashoffset="gaugeOffset(s.runningStatus)"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CorosEstadoEntrenamiento',
  props: {
    status: { type: Object, default: null },
    hoyEntreno: { type: Object, default: null },
  },
  computed: {
    s() {
      return this.status || {};
    },
    fechaHoy() {
      const iso = this.s.today;
      if (!iso) return '';
      const [y, m, d] = iso.split('-').map(Number);
      const dt = new Date(y, m - 1, d);
      return dt.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' });
    },
    weekMax() {
      const loads = (this.s.weekBars || []).map((b) => b.load || 0);
      return Math.max(80, ...loads);
    },
    rollMax() {
      const loads = (this.s.rollingBars || []).map((b) => b.load || 0);
      return Math.max(80, ...loads);
    },
    gaugeArc() {
      return 157;
    },
  },
  methods: {
    fmtInt(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—';
      return String(Math.round(Number(v)));
    },
    fmtDec(v) {
      if (v == null || !Number.isFinite(Number(v))) return '—';
      return Number(v).toFixed(1);
    },
    fmtDuration(sec) {
      if (sec == null) return '';
      const s = Number(sec);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      if (h) return `${h}:${String(m).padStart(2, '0')}:00`;
      return `${m}:00`;
    },
    barHeight(load, max) {
      const n = Number(load) || 0;
      const track = 40;
      if (n <= 0) return '4px';
      const px = Math.round((n / Math.max(Number(max) || 1, 1)) * track);
      return `${Math.max(10, Math.min(track, px))}px`;
    },
    gaugeOffset(value) {
      const n = Math.max(0, Math.min(100, Number(value) || 0));
      return this.gaugeArc * (1 - n / 100);
    },
  },
};
</script>

<style scoped>
.coros-evo {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.coros-hoy {
  background: linear-gradient(135deg, #0d6efd 0%, #084298 100%);
  color: #fff;
}
.coros-hoy-icon {
  width: 2.4rem;
  height: 2.4rem;
  background: rgba(255, 255, 255, 0.18);
}
.coros-metric {
  background: #f8f9fa;
}
.coros-metric-value {
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
}
.coros-status {
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.15;
}
.tone-orange { color: #fd7e14; }
.tone-red { color: #dc3545; }
.tone-blue { color: #0d6efd; }
.tone-teal { color: #0dcaf0; }
.tone-green { color: #198754; }
.tone-muted { color: #6c757d; }
.coros-kpi-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  font-weight: 600;
  color: #6c757d;
}
.coros-bars {
  display: flex;
  align-items: stretch;
  gap: 0.42rem;
  height: 3.6rem;
}
.coros-bar-col {
  width: 0.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
}
.coros-bar-track {
  flex: 1 1 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 0;
}
.coros-bar {
  width: 100%;
  border-radius: 999px;
  background: #dee2e6;
  flex: 0 0 auto;
}
.coros-bar.on {
  background: #0d6efd;
}
.coros-bar.accent.on {
  background: #0dcaf0;
}
.coros-bar-letter {
  font-size: 0.62rem;
  line-height: 1;
  color: #6c757d;
  flex-shrink: 0;
}
.coros-gauge {
  width: 7.2rem;
  flex-shrink: 0;
}
</style>
